/* Admin dashboard with statistics, revenue chart, and top products in the shared admin page frame.
   Updated: batch-loaded monthly data in a single query and batch-loaded top product images to eliminate N+1 patterns. */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { FileText, Package, ShoppingBag, Euro, ArrowUpRight } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import { getProductImageUrl } from '@/lib/image-url';

interface Stats {
  products: number;
  faqs: number;
  activeProducts: number;
  totalVariants: number;
  totalOrders: number;
  pendingOrders: number;
  totalRevenue: number;
  totalBlogs: number;
}

interface MonthlyData {
  month: string;
  revenue: number;
  transactions: number;
}

interface TopProduct {
  id: string;
  name: string;
  thumbnail: string;
  usesNewBucket: boolean;
  sales: number;
  revenue: number;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats>({ products: 0, faqs: 0, activeProducts: 0, totalVariants: 0, totalOrders: 0, pendingOrders: 0, totalRevenue: 0, totalBlogs: 0 });
  const [loading, setLoading] = useState(true);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [topProductsSortBy, setTopProductsSortBy] = useState<'quantity' | 'revenue'>('quantity');

  useEffect(() => {
    loadStats();
    loadMonthlyData();
    loadTopProducts();
  }, []);

  const loadStats = async () => {
    try {
      const [productsResult, faqsResult, activeProductsResult, variantsResult, ordersResult, pendingOrdersResult, blogsResult] = await Promise.all([
        supabase.from('products').select('id', { count: 'exact', head: true }),
        supabase.from('faqs').select('id', { count: 'exact', head: true }),
        supabase.from('products').select('id', { count: 'exact', head: true }).eq('active', true),
        supabase.from('product_variants').select('id', { count: 'exact', head: true }),
        supabase.from('orders').select('id', { count: 'exact', head: true }),
        supabase.from('orders').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('blogs').select('id', { count: 'exact', head: true })
      ]);

      const { data: revenueData } = await supabase
        .from('orders')
        .select('total')
        .neq('status', 'cancelled');

      const totalRevenue = revenueData?.reduce((sum: number, order: any) => sum + parseFloat(order.total.toString()), 0) || 0;

      setStats({
        products: productsResult.count || 0,
        faqs: faqsResult.count || 0,
        activeProducts: activeProductsResult.count || 0,
        totalVariants: variantsResult.count || 0,
        totalOrders: ordersResult.count || 0,
        pendingOrders: pendingOrdersResult.count || 0,
        totalRevenue,
        totalBlogs: blogsResult.count || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyData = async () => {
    try {
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString();

      const { data: orders } = await supabase
        .from('orders')
        .select('total, created_at')
        .neq('status', 'cancelled')
        .gte('created_at', startDate);

      const months: MonthlyData[] = [];
      for (let i = 3; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
        const monthOrders = (orders || []).filter(o => {
          const created = new Date(o.created_at);
          return created >= date && created < nextMonth;
        });
        const revenue = monthOrders.reduce((sum, order) => sum + parseFloat(order.total.toString()), 0);
        months.push({
          month: date.toLocaleDateString('nl-NL', { month: 'short' }),
          revenue: Math.round(revenue * 100) / 100,
          transactions: monthOrders.length
        });
      }

      setMonthlyData(months);
    } catch (error) {
      console.error('Error loading monthly data:', error);
    }
  };

  const loadTopProducts = async () => {
    try {
      const { data: orderItems, error: orderError } = await supabase
        .from('order_items')
        .select('product_variant_id, product_name, variant_size, quantity, unit_price');

      if (orderError) {
        console.error('Error fetching order items:', orderError);
        return;
      }

      if (!orderItems || orderItems.length === 0) {
        return;
      }

      const productMap = new Map<string, { sales: number; revenue: number; variantId: string }>();

      orderItems.forEach((item: any) => {
        const key = `${item.product_name}|${item.variant_size || ''}`;

        if (productMap.has(key)) {
          const existing = productMap.get(key)!;
          existing.sales += item.quantity;
          existing.revenue += parseFloat(item.unit_price.toString()) * item.quantity;
        } else {
          productMap.set(key, {
            sales: item.quantity,
            revenue: parseFloat(item.unit_price.toString()) * item.quantity,
            variantId: item.product_variant_id
          });
        }
      });

      const sortedProducts = Array.from(productMap.entries())
        .map(([key, value]) => {
          const [productName, variantSize] = key.split('|');
          return {
            id: key,
            name: variantSize ? `${productName} - ${variantSize}` : productName,
            sales: value.sales,
            revenue: value.revenue,
            variantId: value.variantId
          };
        })
        .sort((a, b) => b.revenue - a.revenue);

      const variantIds = sortedProducts
        .map(p => p.variantId)
        .filter((id): id is string => !!id);

      const uniqueVariantIds = [...new Set(variantIds)];

      let variantToProductMap = new Map<string, string>();
      let productToImageMap = new Map<string, { storage_path: string; uses_new_bucket: boolean }>();

      if (uniqueVariantIds.length > 0) {
        const [variantsRes, imagesRes] = await Promise.all([
          supabase
            .from('product_variants')
            .select('id, product_id')
            .in('id', uniqueVariantIds),
          supabase
            .from('product_images')
            .select('product_id, storage_path, uses_new_bucket')
            .in('product_id', (
              await supabase
                .from('product_variants')
                .select('product_id')
                .in('id', uniqueVariantIds)
            ).data?.map((v: any) => v.product_id) || [])
            .eq('is_primary', true),
        ]);

        (variantsRes.data || []).forEach((v: any) => {
          variantToProductMap.set(v.id, v.product_id);
        });

        (imagesRes.data || []).forEach((img: any) => {
          if (!productToImageMap.has(img.product_id)) {
            productToImageMap.set(img.product_id, { storage_path: img.storage_path, uses_new_bucket: img.uses_new_bucket });
          }
        });
      }

      const productsWithImages = sortedProducts.map((product) => {
        const productId = product.variantId ? variantToProductMap.get(product.variantId) : undefined;
        const image = productId ? productToImageMap.get(productId) : undefined;
        return {
          ...product,
          thumbnail: image?.storage_path || '',
          usesNewBucket: image?.uses_new_bucket || false
        };
      });

      setTopProducts(productsWithImages);
    } catch (error) {
      console.error('Error loading top products:', error);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const statsCards = [
    {
      title: 'Totale omzet',
      value: formatPrice(stats.totalRevenue),
      icon: Euro,
      href: '/kh-admin/orders'
    },
    {
      title: 'Totaal bestellingen',
      value: stats.totalOrders,
      icon: ShoppingBag,
      href: '/kh-admin/orders'
    },
    {
      title: 'Blogs',
      value: stats.totalBlogs,
      icon: FileText,
      href: '/kh-admin/blogs'
    },
    {
      title: 'Actieve producten',
      value: stats.activeProducts,
      icon: Package,
      href: '/kh-admin/assortiment'
    }
  ];

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-[#1e2b22] mb-2">Dashboard</h1>
          <p className="text-sm text-[#5a6552]">Overzicht van omzet, bestellingen en topproducten.</p>
        </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statsCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <Card className="border border-[#e7e1d6] bg-white shadow-sm hover:shadow-md transition-all relative group cursor-pointer overflow-hidden" onClick={() => navigate(stat.href)}>
                    <CardContent className="p-6 rounded-lg transition-colors group-hover:bg-[#e7e1d6]/30">
                      <div className="absolute top-0 right-0 bg-[#1e2b22] text-white p-3 rounded-bl-xl pointer-events-none">
                        <ArrowUpRight className="w-5 h-5 transition-all group-hover:rotate-[45deg] group-hover:stroke-[3]" />
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="bg-[#e7e1d6] text-[#1e2b22] p-2 rounded-lg">
                          <Icon className="w-4 h-4" />
                        </div>
                        <p className="text-sm font-semibold text-[#5a6552]">{stat.title}</p>
                      </div>
                      <p className="text-3xl font-bold text-[#1e2b22]">{loading ? '...' : stat.value}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Detailed Stats Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8 items-stretch">
            {/* Revenue & Transactions Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="lg:col-span-8"
            >
              <Card className="border border-[#e7e1d6] bg-white shadow-sm h-full">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-[#1e2b22]">Bestellingen</CardTitle>
                  <div className="flex items-center gap-6 mt-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm bg-[#1e2b22]"></div>
                      <span className="text-sm text-[#5a6552]">
                        Omzet
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-[#5a6552]"></div>
                      <span className="text-sm text-[#5a6552]">
                        Bestellingen
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e7e1d6" />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: '#5a6552', fontSize: 12 }}
                        axisLine={{ stroke: '#e7e1d6' }}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fill: '#5a6552', fontSize: 12 }}
                        axisLine={{ stroke: '#e7e1d6' }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: '#5a6552', fontSize: 12 }}
                        axisLine={{ stroke: '#e7e1d6' }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#fcfaf6',
                          border: '1px solid #e7e1d6',
                          borderRadius: '8px',
                          padding: '12px',
                          color: '#1e2b22'
                        }}
                        formatter={(value: any, name: string) => {
                          if (name === 'revenue') {
                            return [formatPrice(value), 'Omzet'];
                          }
                          return [value, 'Bestellingen'];
                        }}
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="revenue"
                        fill="#1e2b22"
                        radius={[8, 8, 0, 0]}
                        barSize={40}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="transactions"
                        stroke="#5a6552"
                        strokeWidth={3}
                        dot={{ fill: '#5a6552', r: 5 }}
                        activeDot={{ r: 7 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </motion.div>

            {/* Top 5 Products */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="lg:col-span-4"
            >
              <Card className="border border-[#e7e1d6] bg-white shadow-sm h-full">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl font-bold text-[#1e2b22]">Top 5 producten</CardTitle>
                    <Select value={topProductsSortBy} onValueChange={(value: 'quantity' | 'revenue') => setTopProductsSortBy(value)}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quantity">Aantal</SelectItem>
                        <SelectItem value="revenue">Omzet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {topProducts.length === 0 ? (
                      <p className="text-sm text-[#5a6552] text-center py-8">Geen data beschikbaar</p>
                    ) : (
                      [...topProducts]
                        .sort((a, b) => topProductsSortBy === 'quantity' ? b.sales - a.sales : b.revenue - a.revenue)
                        .slice(0, 5)
                        .map((product, index) => (
                          <div
                            key={product.id}
                            className="flex items-center gap-3 pb-4 border-b border-[#e7e1d6] last:border-b-0 last:pb-0"
                          >
                            <div className="w-8 text-center">
                              <span className="text-lg font-bold text-[#cfcac2]">{index + 1}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-[#1e2b22] truncate">{product.name}</p>
                              <p className="text-xs text-[#5a6552]">{product.sales} verkocht</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-[#1e2b22]">{formatPrice(product.revenue)}</p>
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

      </div>
    </>
  );
}
